import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ScriptLoaderService } from './script-loader.service';
import { HttpWebApiService } from './http-web-api.service';
import { HttpRequestSettings, apis } from '@sb-shared/models/request-settings';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class PaymentsService {

  private urlBase = 'WebPayments/';
  private config: HttpRequestSettings = {
    api: apis.Web,
    // Payment operations have their own UX (navigate to payment-complete, show
    // their own errors). Without this, HttpWebApiService pops a generic "saved"
    // success toast on every POST — which the AngularJS baseline never showed.
    showSuccessToast: false
  };

  constructor(
    private scriptSvc: ScriptLoaderService,
    private http: HttpWebApiService
  ) { }

  loadPaymentProviderExternalScripts()
  {
    environment.paymentProviderConfigs.forEach(providerConfig =>
      {
        this.scriptSvc.loadJsScript(
          providerConfig.script.url,
          providerConfig.script.crossOriginAttribute ?? null,
          providerConfig.script.integrityAttribute ?? null
        )
      });
    }

  organisationHasPaymentProviders(): Observable<boolean> {
    return this.http.get(this.urlBase + 'OrganisationHasPaymentProviders', this.config);
  }

  chargeProviderWithToken(token: string, merchantReference: string): Observable<any> {
    return this.http.post(this.urlBase + 'ChargeProviderWithToken', { token, merchantReference }, this.config);
  }

  cancelPaymentSession(merchantReference: string): Observable<any> {
    // Backend WebPayments/CancelPaymentSession is an [HttpGet] taking a
    // `merchantReference` query param (the session sid). Posting a body here
    // 405s/400s, which surfaced as an error when cancelling a (voucher) payment.
    return this.http.get(this.urlBase + 'CancelPaymentSession', {
      ...this.config,
      params: { merchantReference }
    });
  }

  submitPaymentProviderlessTransaction(signatureRequest: any): Observable<any> {
    return this.http.post(this.urlBase + 'SubmitPaymentProviderlessTransaction', signatureRequest, this.config);
  }

  createPaymentSession(signatureRequest: any): Observable<any> {
    return this.http.post(this.urlBase + 'CreatePaymentSession', signatureRequest, this.config);
  }

  getPaymentSessionResult(paymentProviderId: number, sessionId: string): Observable<any> {
    return this.http.get(this.urlBase + 'GetPaymentSessionResult', {
      ...this.config,
      params: { paymentProvider: paymentProviderId, sessionId }
    });
  }

  getPaymentReceiptByMerchantReference(merchantReference: string): Observable<any> {
    return this.http.get(this.urlBase + 'GetReceiptByMerchantReference', {
      ...this.config,
      params: { merchantReference }
    });
  }

  getPaymentReceiptByTransactions(items: any): Observable<any> {
    return this.http.post(this.urlBase + 'GetReceiptByTransactions', { items }, this.config);
  }

  getPaymentProvidersConfigForOrganisation(): Observable<any> {
    return this.http.get(this.urlBase + 'PaymentProvidersConfigForOrganisation', this.config);
  }
}
